import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchTasksData } from '../../services/dashboard/TasksHistoryService';
import { TaskHistoryItem } from '../tasks-list/TaskHistoryModel';

interface TasksState {
    data: TaskHistoryItem[] | null;
    loading: boolean;
    error: string | null;
    hasMoreData: boolean;
}
const initialState: TasksState = {
    data: null,
    loading: false,
    error: null,
    hasMoreData: true,
};

export const fetchTasksDataAsync = createAsyncThunk<
    { taskItems: TaskHistoryItem[]; append: boolean },
    {
        assetId: string;
        startDate?: string;
        endDate?: string;
        filterBy?: string;
        filterValue?: string;
        orderBy?: string;
        orderColumn?: string;
        appendData?: boolean;
    }
>('tasks/fetchTasksDataAsync', async (params) => {
    const {
        assetId,
        startDate,
        endDate,
        filterBy,
        filterValue,
        orderBy,
        orderColumn,
        appendData,
    } = params;

    const data = await fetchTasksData(
        assetId,
        startDate,
        endDate,
        filterBy,
        filterValue,
        orderBy,
        orderColumn
    );

    return { taskItems: data, append: appendData ?? false };
});

const TasksHistorySlice = createSlice({
    name: 'TasksHistorySlice',
    initialState,
    reducers: {
        resetTasksState: (state) => {
            state.data = null;
            state.error = null;
            state.loading = false;
            state.hasMoreData = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTasksDataAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTasksDataAsync.fulfilled, (state, action) => {
                state.loading = false;
                const newItems = action.payload.taskItems;

                if (action.payload.append && state.data) {
                    state.data = [...state.data, ...newItems];
                } else {
                    state.data = newItems;
                }

                // If we received fewer items than expected (e.g., less than page size), 
                // assume no more data is available
                state.hasMoreData = newItems.length > 0; // You might want to adjust this logic based on your API
            })
            .addCase(fetchTasksDataAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'Failed to fetch tasks';
            });
    },
});

export const { resetTasksState } = TasksHistorySlice.actions;
export default TasksHistorySlice.reducer;
